<template>
  <modal-cmp
    ref="modal"
    class="SearchModalCmp"
    data-name="search-modal"
    :data-has-close-button="false"
    @opened="handleOpened"
  >
    <loading-cmp
      :class="['modal-content', { loading }]"
      :is-loading="loading"
      :role="loading ? 'alert' : null"
      :aria-live="loading ? 'assertive' : null"
    >
      <div class="container search-container">
        <div class="row">
          <div class="col-12">
            <div class="head">
              <div class="search-bar">
                <div class="logo">
                  <a
                    :href="homePageUrl"
                    class="logo"
                    :aria-label="dataWebsiteName"
                  >
                    <svg>
                      <use href="#icon-logo" />
                    </svg>
                  </a>
                </div>
                <form-cmp class="search-form">
                  <input-text-cmp-extended
                    ref="searchInput"
                    v-model.trim="$v.search.$model"
                    :data-error-occurred="
                      !$v.search.serverError ||
                      !!globalErrors.length ||
                      !!Object.keys(serverErrors).length
                    "
                    :data-errors="[
                      {
                        condition: !$v.search.serverError,
                        message: serverErrors.search,
                      },
                      ...globalErrors,
                    ]"
                    class="icon-align-left"
                    data-name="search"
                    :data-placeholder="dataSearchPlaceholder"
                    :data-aria-label="dataSearchAriaLabel"
                  >
                    <template #icon>
                      <svg>
                        <use href="#icon-search" />
                      </svg>
                    </template>
                    <template #button>
                      <submit-cmp-extended
                        :data-aria-label="dataSearchAriaLabel"
                        :data-disabled="disableSubmit || loading"
                        @click-submit="onSubmitForm"
                      >
                        <svg v-if="!loading">
                          <use href="#icon-arrow-right" />
                        </svg>
                        <svg v-else>
                          <use href="#icon-ellipsis-loader" />
                        </svg>
                      </submit-cmp-extended>
                    </template>
                  </input-text-cmp-extended>
                </form-cmp>
                <button
                  class="modal-close"
                  type="button"
                  :aria-label="dataCloseAriaLabel"
                  @click="$modal.hide('search-modal')"
                >
                  <span class="sr-only">{{ dataCloseAriaLabel }}</span>
                </button>
              </div>
              <div v-if="submittedValue" class="resultsFor">
                <span class="a-paragraph-2"><slot name="results-for" /></span>
                <span class="n-heading-3 text-uppercase">
                  {{ submittedValue }}
                </span>
              </div>
              <tabs-cmp
                :data-active-tab-id="activeCategoryId"
                :data-tabs="categories"
                :data-no-content="true"
                @update:data-active-tab-id="handleChangeCategory"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="container results-wrapper">
        <div class="row">
          <div class="col-12 py-r py-md-l">
            <p
              v-if="showDescription && !loading"
              class="a-paragraph-2 text-center"
            >
              <slot name="description" />
            </p>
            <div v-else-if="!loading" class="results-container">
              <transition name="category-results">
                <div
                  v-for="category in activeCategory"
                  :key="`cat-${category.id}`"
                  class="category-results"
                >
                  <p
                    v-if="filteredResults[category.id].results.length === 0"
                    class="a-paragraph-2 text-center"
                  >
                    <slot name="no-results" />
                  </p>
                  <div class="row justify-content-center">
                    <div
                      :class="[
                        'col-12',
                        {
                          'col-md-11':
                            activeCategoryId === 'Products' ||
                            activeCategoryId === 'NewsAndMedia',
                        },
                      ]"
                    >
                      <div
                        class="row row-cols-1 row-cols-md-3 category-results-grid"
                      >
                        <div
                          v-for="(item, index) in shownResults"
                          :key="`col-${index}-${category.id}`"
                          :class="['col', { ['result-card-' + index]: true }]"
                        >
                          <card-product-cmp
                            v-if="activeCategoryId === 'Products'"
                            class="product animationForTransparentsImgs h-100"
                            :data-href="item.href"
                          >
                            <template #seo-link>
                              <a :href="item.href">{{ item.title }}</a>
                            </template>
                            <template #title>
                              <h3>{{ item.title }}</h3>
                            </template>
                            <template #image>
                              <image-cmp
                                width="290"
                                height="240"
                                :data-desktop-image="item.image"
                                :data-image-alt="item.image_alt"
                              />
                            </template>
                            <template #text>
                              <span class="a-support text-color-grey-800 pt-xs">
                                {{ item.ancestor_title }}
                              </span>
                            </template>
                          </card-product-cmp>
                          <card-cmp
                            v-if="activeCategoryId === 'Recipes'"
                            :data-href="item.href"
                            class="recipe h-100"
                          >
                            <template #seo-link>
                              <a :href="item.href">{{ item.list_title }}</a>
                            </template>
                            <template #image>
                              <image-cmp
                                width="1062"
                                height="596"
                                :data-desktop-image="item.list_image"
                                :data-image-alt="item.list_image_alt"
                              />
                            </template>
                            <template #tags>
                              <a
                                v-for="tag in item.categories"
                                :key="`tag-${tag.slug}-${item.slug}`"
                                :href="tag.href"
                              >
                                <tag-cmp>
                                  {{ tag.name }}
                                </tag-cmp>
                              </a>
                            </template>
                            <template #title>
                              <h2>{{ item.list_title }}</h2>
                            </template>
                            <template #text>
                              {{ item.list_short_title }}
                            </template>
                          </card-cmp>
                          <card-cmp
                            v-if="activeCategoryId === 'NewsAndMedia'"
                            :data-href="item.href"
                            class="normal h-100"
                          >
                            <template #seo-link>
                              <a :href="item.href">{{ item.title }}</a>
                            </template>
                            <template #image>
                              <image-cmp
                                width="1062"
                                height="596"
                                :data-desktop-image="item.list_image"
                                :data-image-alt="item.list_image_alt"
                              />
                            </template>
                            <template
                              v-if="item.topics && item.topics.length"
                              #tags
                            >
                              <tag-cmp
                                v-for="topic in item.topics"
                                :key="`card-${item.slug}-${_uid}-${topic.slug}`"
                              >
                                {{ topic.label }}
                              </tag-cmp>
                            </template>
                            <template #category>
                              <h3>{{ item.category.title }}</h3>
                            </template>
                            <template #title>
                              <h2>{{ item.title }}</h2>
                            </template>
                            <template #date>
                              <time :datetime="item.publication_start_iso">
                                <beautiful-date
                                  :data-datetime="item.publication_start_iso"
                                  :data-locale="currentLang.language_code"
                                  :data-format="$store.state.config.date_format"
                                >
                                  {{ item.publication_start }}
                                </beautiful-date>
                              </time>
                            </template>
                          </card-cmp>
                        </div>
                      </div>
                    </div>
                  </div>
                  <cta-button-cmp-extended
                    v-if="
                      filteredResults[category.id].results.length >=
                      numberOfResultsPerLoad *
                        filteredResults[category.id].loadsCounter
                    "
                    class="btn-primary mt-l load-more"
                    data-tag="button"
                    @click="handleLoadMore(category.id)"
                  >
                    <slot name="load-more" />
                  </cta-button-cmp-extended>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </loading-cmp>
  </modal-cmp>
</template>

<script>
import Vue from 'vue';
import Vuelidate from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { castPropToType } from '@/helpers/cms-support';
import { getCurrentSiteHref, getCurrentLangConf } from '@/helpers/utils';
import { FormCmp } from '@frankhoodbs/lib';
import FormElementsSupport from '@frankhoodbs/lib/src/mixins/form-elements-support';
import BeautifulDate from './beautiful-date.vue';
import CardCmp from './card-cmp.vue';
import CardProductCmp from './card-product-cmp.vue';
import CtaButtonCmpExtended from './cta-button-cmp-extended.vue';
import ImageCmp from './image-cmp.vue';
import InputTextCmpExtended from './input-text-cmp-extended.vue';
import ModalCmp from './modal-cmp';
import SubmitCmpExtended from './submit-cmp-extended.vue';
import TagCmp from './tag-cmp.vue';
import axios from 'axios';

Vue.use(Vuelidate);

const checkServerErrors = (param) => {
  return (value, vm) => {
    return Object.keys(vm.serverErrors).indexOf(param) === -1;
  };
};

export default {
  name: 'SearchModalCmp',
  components: {
    BeautifulDate,
    CardCmp,
    TagCmp,
    CardProductCmp,
    ImageCmp,
    CtaButtonCmpExtended,
    SubmitCmpExtended,
    InputTextCmpExtended,
    FormCmp,
    ModalCmp,
  },
  mixins: [FormElementsSupport],
  props: {
    dataConfig: {
      type: [Object, String],
      required: true,
    },
    dataWebsiteName: {
      type: String,
      default: '',
    },
    dataCloseAriaLabel: {
      type: String,
      default: 'Close',
    },
    dataSearchAriaLabel: {
      type: String,
      default: 'Search',
    },
    dataSearchPlaceholder: {
      type: String,
      default: 'Search',
    },
    dataProductsEnabled: {
      type: [Boolean, String],
      default: true,
    },
    dataProductsLabel: {
      type: String,
      default: 'Products',
    },
    dataRecipesEnabled: {
      type: [Boolean, String],
      default: true,
    },
    dataRecipesLabel: {
      type: String,
      default: 'Recipes',
    },
    dataNewsAndMediaEnabled: {
      type: [Boolean, String],
      default: true,
    },
    dataNewsAndMediaLabel: {
      type: String,
      default: 'News & Media',
    },
  },
  setup() {
    return { castPropToType };
  },
  data() {
    return {
      showDescription: true,
      activeCategoryId: '',
      search: null,
      submittedValue: '',
      filteredResults: {
        Products: {
          results: [],
          loadsCounter: 1,
        },
        Recipes: {
          results: [],
          loadsCounter: 1,
        },
        NewsAndMedia: {
          results: [],
          loadsCounter: 1,
        },
      },
      numberOfResultsPerLoad: 12,
    };
  },
  computed: {
    conf() {
      return this.dataConfig
        ? this.castPropToType(this.dataConfig, 'object')
        : null;
    },
    productsEnabled() {
      return this.dataProductsEnabled
        ? this.castPropToType(this.dataProductsEnabled, 'boolean')
        : null;
    },
    recipesEnabled() {
      return this.dataRecipesEnabled
        ? this.castPropToType(this.dataRecipesEnabled, 'boolean')
        : null;
    },
    newsAndMediaEnabled() {
      return this.dataNewsAndMediaEnabled
        ? this.castPropToType(this.dataNewsAndMediaEnabled, 'boolean')
        : null;
    },
    currentLang() {
      return getCurrentLangConf(this.$store);
    },
    homePageUrl() {
      return getCurrentSiteHref(this.$store);
    },
    disableSubmit() {
      if (this.$v && (this.$v.$anyError || this.$v.$invalid)) {
        return true;
      }

      return false;
    },
    activeCategory() {
      return this.categories.filter((e) => e.id === this.activeCategoryId);
    },
    categories() {
      return Object.keys(this.filteredResults).reduce((acc, key) => {
        if (
          (this.productsEnabled && key === 'Products') ||
          (this.recipesEnabled && key === 'Recipes') ||
          (this.newsAndMediaEnabled && key === 'NewsAndMedia')
        ) {
          acc.push({
            id: key,
            label: `${
              key === 'Products'
                ? this.dataProductsLabel
                : key === 'Recipes'
                ? this.dataRecipesLabel
                : this.dataNewsAndMediaLabel
            }${
              this.filteredResults[key].results.length > 0
                ? ` (${this.filteredResults[key].results.length})`
                : ''
            }`,
          });
        }

        return acc;
      }, []);
    },
    shownResults() {
      return this.activeCategoryId
        ? this.filteredResults[this.activeCategoryId].results.slice(
            0,
            this.numberOfResultsPerLoad *
              this.filteredResults[this.activeCategoryId].loadsCounter
          )
        : null;
    },
  },
  validations() {
    return {
      search: {
        required,
        serverError: checkServerErrors('search'),
      },
    };
  },
  watch: {
    submitted: {
      handler(newVal, oldVal) {
        if (!newVal && oldVal) {
          // This indicates that a send request has been made
          const key = ((obj) =>
            Object.entries(obj)[0][1]?.results?.length === 0
              ? Object.entries(obj)
                  .slice(1)
                  .find(([_, v]) => v?.results?.length > 0)?.[0]
              : false)(this.filteredResults);
          if (key) {
            this.handleChangeCategory(key);
          }
        }
      },
    },
    shownResults: {
      handler(newVal, oldVal) {
        if (newVal.length ?? newVal.length > oldVal.length) {
          this.$nextTick(() => {
            const firstNewElement = document.querySelector(
              `.result-card-${oldVal.length ? oldVal.length : 0}`
            );
            const focusableElements = firstNewElement.querySelectorAll(
              'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
            );
            this.$refs.modal.trapFocus(focusableElements[0]);
          });
        }
      },
    },
  },
  mounted() {
    this.activeCategoryId = this.productsEnabled
      ? 'Products'
      : this.recipesEnabled
      ? 'Recipes'
      : 'NewsAndMedia';
  },
  methods: {
    async onSubmitForm() {
      if (!this.submitted && !this.disableSubmit && !this.$v.$invalid) {
        this.submitted = true;
        this.submittedValue = this.search;
        Object.keys(this.filteredResults).forEach((key) => {
          this.filteredResults[key].results = [];
          this.filteredResults[key].loadsCounter = 1;
        });
        this.loading = true;
        this.globalErrors = [];
        this.toastErrors = [];

        try {
          const response = await axios.get(
            `${this.conf.search_api}?s=${this.search}`
          );

          if (response.data) {
            response.data.forEach((e) => {
              if (this.filteredResults[e.type]) {
                this.filteredResults[e.type].results.push(e);
              }
            });
            this.showDescription = false;
          } else {
            this.$_formElementsSupport_handleApiError(response);
          }
        } catch (e) {
          const hasResponse =
            Object.getOwnPropertyNames(e).includes('response');
          if (
            !hasResponse ||
            (hasResponse && ![400, 401, 403].includes(e.response.status))
          ) {
            // 500 or unknown status code
            this.$_formElementsSupport_handleUnknownError(e);
          } else {
            this.$_formElementsSupport_handleErrors(e);
          }
        } finally {
          this.loading = false;
          this.submitted = false;
        }
      }
    },
    handleChangeCategory(catId) {
      this.activeCategoryId = catId;
    },
    handleLoadMore(catId) {
      this.filteredResults[catId].loadsCounter++;
    },
    handleOpened() {
      const inputEl = this.$refs.searchInput.$el.querySelector('input');
      this.$refs.modal.trapFocus(inputEl);
    },
  },
};
</script>

<style lang="scss" scoped>
@mixin on-custom-hover() {
  @media (hover: hover),
    (-ms-high-contrast: none),
    (-ms-high-contrast: active) {
    &:hover {
      @content;
    }
  }

  @media (hover: none) {
    &:active {
      @content;
    }
  }
}

.SearchModalCmp {
  --header-padding-inline: 24px;

  @include media-breakpoint-up(lg) {
    --header-padding-inline: 32px;
  }

  @include media-breakpoint-up(xl) {
    --header-padding-inline: 80px;
  }

  ::v-deep .modal-container {
    width: 100% !important;
    max-width: none !important;
    justify-content: flex-start !important;

    .modal-box {
      width: 100% !important;
      height: 100% !important;
      max-height: 100vh !important;
      max-height: 100svh !important;
      border-radius: 0 !important;
      filter: none !important;
    }
  }

  .modal-content {
    border-top: 0 !important;

    .search-container {
      @include media-breakpoint-down(md) {
        --bfp-gutter-x: 0px;

        & > .row {
          --bfp-gutter-x: 0px;
        }
      }
    }

    .head {
      padding-top: 12px;
      background-color: $color-blue;

      @include media-breakpoint-up(md) {
        border-radius: 0 0 8px 8px;
      }

      .search-bar {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        row-gap: 16px;
        padding-inline: var(--header-padding-inline);
        padding-bottom: 35px;
        --logo-width: 76.41px;
        --logo-height: 48px;

        @include media-breakpoint-up(md) {
          flex-direction: row;
          align-items: center;
        }

        @include media-breakpoint-up(lg) {
          --logo-width: 101.02px;
          --logo-height: 64px;
        }

        @include media-breakpoint-up(xl) {
          --logo-width: 113.65px;
          --logo-height: 72px;
        }

        .logo {
          display: block;

          svg {
            width: var(--logo-width);
            height: var(--logo-height);
            color: $color-white;
          }
        }

        ::v-deep .search-form {
          @include media-breakpoint-up(md) {
            position: relative;
            left: calc(var(--logo-width) * -0.5);
            width: min(66%, 720px);
            margin: 0 auto;
          }

          .InputTextCmpExtended {
            --form-element-input-border-width: 0;
            --form-element-input-border-radius: 4px 0 0 4px;
            --form-element-input-padding-left: 16px;
            --form-element-input-padding-right: 16px;
            --form-element-input-padding-top: 13px;
            --form-element-input-padding-bottom: 13px;
            --form-element-input-color: #{$color-blue};
            --form-element-input-hover-color: #{$color-blue};
            --form-element-input-focus-color: #{$color-blue};
            --form-element-placeholder-color: #{$color-grey-600};
            --form-element-placeholder-hover-color: #{$color-grey-400};
            --form-element-placeholder-font-weight: #{$fh-semi-bold};
            --form-element-icon-width: 18px;
            --form-element-icon-height: 18px;
            --form-element-icon-color: #{$color-blue};

            @include media-breakpoint-up(md) {
              --form-element-input-padding-top: 12px;
              --form-element-input-padding-bottom: 12px;
            }

            .form-control__wrapper {
              border-radius: 4px;

              .form-control {
                --form-element-input-font-weight: #{$fh-semi-bold};
              }
            }
          }

          .SubmitCmpExtended {
            --form-element-width: 56px;
            --form-element-input-border-radius: 0 4px 4px 0;
            --form-element-icon-width: 24px;
            --form-element-icon-height: 24px;
            flex-shrink: 0;

            .form-control__wrapper {
              height: 100%;
              border-radius: 0 4px 4px 0;
            }

            .CtaButtonCmp {
              --cta-button-width: 100%;
              --cta-button-height: 100%;
              --cta-button-padding: 0;
              --cta-button-border-width: 0;
              --cta-button-hover-border-width: 0;
              --cta-button-hover-bg-color: #{$color-grey-200-50};
              --cta-button-disabled-border-width: 0;
              --cta-button-border-radius: 0 4px 4px 0;
              top: 0 !important;

              &:disabled,
              &.disabled {
                --form-element-icon-color: #{$color-grey-600};
              }
            }
          }
        }

        .modal-close {
          position: absolute;
          top: calc(var(--logo-height) * 0.5);
          right: var(--header-padding-inline);
          margin-top: -12px;
          width: 24px;
          height: 24px;
          padding: 0;
          border: 1px solid $color-white;
          border-radius: 50%;
          background: none;
          cursor: pointer;
          z-index: 999999;
          transition: border-color 0.3s ease;

          &::before,
          &::after {
            content: '';
            display: block;
            position: absolute;
            left: 50%;
            top: 50%;
            width: 10px;
            height: 1px;
            background: $color-white;
            margin: 0 0 0 -5px; /* -half-height 0 0 -half-width */
            transition: background-color 0.3s ease;
          }

          &::before {
            transform: rotate(45deg);
          }

          &::after {
            transform: rotate(-45deg);
          }

          @include on-custom-hover() {
            border-color: $color-grey-400-75;

            &::before,
            &::after {
              background: $color-grey-400-75;
            }
          }

          .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            border: 0;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
          }
        }
      }

      .resultsFor {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding-inline: var(--header-padding-inline);
        padding-bottom: 43px;
        color: $color-white;
      }

      .TabsCmp {
        padding-top: 21px;

        @include media-breakpoint-up(md) {
          width: min(66%, 720px);
          margin: 0 auto;
        }

        ::v-deep .ScrollXNavbarCmp {
          ul {
            li.tab {
              position: relative;
              min-width: auto !important;
              margin: 0 !important;
              padding: 0 0 19px !important;
              user-select: none;

              @include media-breakpoint-up(md) {
                min-width: 99px !important;
              }

              &::after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 3px;
                background-color: $color-white;
                opacity: 0;
                transition: opacity 0.3s;

                @include media-breakpoint-up(md) {
                  border-radius: 24px 24px 0 0;
                }
              }

              .tab-label {
                padding: 0 16px;
                white-space: nowrap;
              }

              &.active {
                border-bottom: 0;

                &::after {
                  opacity: 1;
                }

                .tab-label {
                  color: $color-white;
                }
              }

              &:hover:not(.active) {
                .tab-label {
                  color: $color-grey-600-75;
                }
              }
            }
          }

          &::after {
            content: none;
          }
        }
      }
    }

    .results-wrapper {
      @include media-breakpoint-down(md) {
        --bfp-gutter-x: 64px;
        padding-block: 8px;

        & > .row {
          --bfp-gutter-x: 64px;
        }
      }
    }

    .results-container {
      display: flex;
      justify-content: flex-end; /* fix jerky movements of the contents */
      width: 100%;
      overflow: hidden; /* fix jerky movements of the contents */

      .category-results {
        flex: 1 0 100%;
        width: 100%;

        &.category-results-enter-active {
          transition: opacity 0.6s ease-in;
          transition-delay: 0.3s;
        }

        &.category-results-leave-active {
          transition: opacity 0.3s ease-out;
        }

        &.category-results-enter,
        &.category-results-leave-to {
          opacity: 0;
        }

        .category-results-grid {
          row-gap: 40px;
        }

        .load-more {
          --cta-button-display: flex;
          margin-inline: auto;
        }
      }
    }
  }
}
</style>
